import { showToast } from '@sharedComponents/Toast';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Context notes of a client or platform. `service` is a ClientService or PlatformService instance
// (both expose getContext / updateContext) and must keep the same identity between renders.
// `save(html)` resolves to the saved context, or to null when the save failed.
const useContextData = (service, uuid) => {
    const { t } = useTranslation('bo');
    // Last answer, tagged with its uuid: while another entity loads, nothing of the previous one is exposed.
    const [loaded, setLoaded] = useState({ uuid: null, context: null, error: null });

    useEffect(() => {
        // A late answer for a previous uuid must not overwrite the current context.
        let cancelled = false;
        service
            .getContext(uuid)
            .then((data) => {
                if (!cancelled) setLoaded({ uuid, context: data, error: null });
            })
            .catch((err) => {
                if (!cancelled) setLoaded({ uuid, context: null, error: err });
            });
        return () => {
            cancelled = true;
        };
    }, [service, uuid]);

    const save = useCallback(
        async (html) => {
            try {
                const saved = await service.updateContext(uuid, html);
                // A save that ends after the entity changed must not overwrite the new entity's context.
                setLoaded((current) => (current.uuid === uuid ? { ...current, context: saved } : current));
                showToast({ type: 'success', text: t('The context has been saved.') });
                return saved;
            } catch (err) {
                const text =
                    err?.status === 413
                        ? t('The context is too long to be saved.')
                        : t('An error occurred while saving. Please try again.');
                showToast({ type: 'error', text });
                return null;
            }
        },
        [service, uuid, t],
    );

    const isLoaded = loaded.uuid === uuid;

    return {
        context: isLoaded ? loaded.context : null,
        loading: !isLoaded,
        error: isLoaded ? loaded.error : null,
        save,
    };
};

export default useContextData;
